import { useMemo } from 'react';
import { sortKeys } from '@/constants';
import useSearchParams from '@/hooks/useSearchParams';
import { AiOutlineClose } from 'react-icons/ai';
import { Filter, Sort } from '@/components/icons';
import PriceFilter from '@/components/category/filters/priceFilter';
import FilterItem from '@/components/category/filters/filter-item';
import { useLocale } from 'use-intl';

const FilterModal = ({ type, setModalType, t, filters, isMobile = false }) => {
  const { getFilterObj, setFilterQueryHandler, resetFilterQueryHandler } =
    useSearchParams();
  const locale = useLocale();

  const selectedSort = useMemo(() => {
    const selectedSortCode = new URLSearchParams({
      sortBy: getFilterObj.sortBy || '',
      sortDir: getFilterObj.sortDir || '',
    }).toString();

    return (
      sortKeys.find((item) => item.code === selectedSortCode) || sortKeys[0]
    );
  }, [getFilterObj]);

  const handleSortChange = (code) => {
    setFilterQueryHandler(Object.fromEntries(new URLSearchParams(code || '')));
    setModalType(null);
  };

  return (
    <div className='fixed top-0 left-0 z-[9999] flex w-screen bg-black/60 shadow-2xl'>
      <div className='relative z-50 max-h-screen w-80 overflow-y-auto bg-white'>
        <div className='text-primary sticky top-0 z-50 flex items-center justify-between bg-white p-3'>
          <h3 className='flex items-center gap-2 text-lg font-semibold'>
            {type === 'sortBy' ? (
              <Sort color='#f00909' />
            ) : (
              <Filter color='#f00909' />
            )}
            <span className='text-sm font-semibold'>
              {t(type === 'sortBy' ? 'sort_by_key' : 'filter_key')}
            </span>
          </h3>
          <AiOutlineClose
            onClick={() => setModalType(null)}
            size='18px'
            className='cursor-pointer'
          />
        </div>

        {type === 'sortBy' ? (
          <ul className='p-3 max-sm:p-2'>
            {sortKeys.map(({ code, text }) => (
              <li
                key={code}
                className={`mx-2 my-4.5 flex cursor-pointer items-center justify-between first:mt-2 max-md:mx-0.5 ${
                  selectedSort.code === code ? 'text-primary' : ''
                }`}
                onClick={() => handleSortChange(code)}
              >
                <span className='max-md:text-sm max-sm:text-xs'>
                  {' '}
                  {t(text)}
                </span>

                <svg
                  className={`size-5 shrink-0 ${locale === 'en' ? 'rotate-270' : 'rotate-90'} transition duration-300 max-md:size-3.5 max-sm:size-3`}
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M19 9l-7 7-7-7'
                  />
                </svg>
              </li>
            ))}
          </ul>
        ) : (
          <div className='flex flex-col gap-2 p-2 pb-20'>
            <PriceFilter isMobile={isMobile} />
            {[
              ...filters.categories,
              ...filters?.colors,
              ...filters?.others,
            ].map((filter, idx) => (
              <FilterItem
                key={filter.code}
                data={filter}
                open={idx < 2}
                isMobile={isMobile}
              />
            ))}
          </div>
        )}

        {/* Apply & Reset Buttons (for Filters) */}
        {type === 'filter' && (
          <div className='fixed bottom-0 w-80 bg-white pt-2 pb-4'>
            <div className='grid grid-cols-2'>
              <button
                onClick={() => setModalType(null)}
                className='bg-primary mx-1 flex items-center justify-center gap-2 rounded-lg p-2 text-sm font-semibold text-white'
              >
                <Filter />
                <span>{t('filter_key')}</span>
              </button>
              <button
                onClick={resetFilterQueryHandler}
                className='mx-1 bg-white p-2 text-center text-sm font-semibold'
              >
                {t('cancel_key')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Background Overlay */}
      <div
        onClick={() => setModalType(null)}
        className='bg-lightblack h-screen w-[calc(100vw-320px)]'
        aria-hidden='true'
      />
    </div>
  );
};
export default FilterModal;
